import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Avatar,
  ListItemButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import PublicIcon from '@mui/icons-material/Public';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, setDoc, collection, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { deleteClass } from '../services/classService';
import { getUserDecks, Deck, addUserDeck, getDeckById } from '../services/deckService';
import QrCodeDialog from '../components/QrCodeDialog';
import { getUserProgressForDeck } from '../services/deckService';

interface Class {
  id: string;
  name: string;
  description: string;
  teacherId: string;
  teacherName?: string;
  teacherEmail?: string;
  deckIds: string[];
  createdAt: Date;
  students: string[];
}

interface Teacher {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

const ClassDetailsPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [classData, setClassData] = useState<Class | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [joiningClass, setJoiningClass] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canShare, setCanShare] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [leavingClass, setLeavingClass] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedClassName, setEditedClassName] = useState('');
  const [editedClassDescription, setEditedClassDescription] = useState('');
  const [editedDeckIds, setEditedDeckIds] = useState<string[]>([]);
  const [userDecks, setUserDecks] = useState<Deck[]>([]);
  const [loadingDecks, setLoadingDecks] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loadingTeacher, setLoadingTeacher] = useState(false);
  const [students, setStudents] = useState<{id: string, displayName: string, email: string | null, photoURL: string | null}[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [classDecks, setClassDecks] = useState<Deck[]>([]);
  const [loadingClassDecks, setLoadingClassDecks] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [selectedDecksToSave, setSelectedDecksToSave] = useState<string[]>([]);
  
  const isTeacher = user && classData && classData.teacherId === user.uid;
  const isStudent = user && classData && classData.students.includes(user.uid);
  
  // Base URL for the application - in production, you should use your actual domain
  const baseUrl = window.location.origin;
  const classUrl = `${baseUrl}/classes/${classId}`;

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const showQr = queryParams.get('qr') === 'true';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Оборачиваем fetchTeacherInfo в useCallback
  const fetchTeacherInfo = useCallback(async (teacherId: string) => {
    if (!teacherId) return;
    
    setLoadingTeacher(true);
    try {
      // First check information in the class document
      const classDocRef = doc(db, 'classes', classId as string);
      const classDoc = await getDoc(classDocRef);
      if (classDoc.exists()) {
        const classData = classDoc.data();
        
        // Check for teacher name in the class document
        if (classData.teacherName && classData.teacherName.trim() !== '') {
          setTeacher({
            uid: teacherId,
            displayName: classData.teacherName,
            email: classData.teacherEmail || null,
            photoURL: null
          });
          setLoadingTeacher(false);
          return;
        }
      }
      
      // If no information in the class, check the users collection
      const userDoc = await getDoc(doc(db, 'users', teacherId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        const teacherName = userData.displayName && userData.displayName.trim() !== '' 
          ? userData.displayName 
          : userData.email 
            ? userData.email 
            : 'Unknown teacher';
        
        const teacherEmail = userData.email || null;
        const photoURL = userData.photoURL || null;
            
        setTeacher({
          uid: teacherId,
          displayName: teacherName,
          email: teacherEmail,
          photoURL: photoURL
        });
        
        // Обновляем документ класса, только если текущий пользователь - преподаватель этого класса
        if (user && user.uid === teacherId) {
          try {
            await updateDoc(classDocRef, {
              teacherName: teacherName,
              teacherEmail: teacherEmail
            });
          } catch (updateError) {
            console.error('Error updating class document:', updateError);
          }
        }
        setLoadingTeacher(false);
        return;
      } 
      
      // Если информация не найдена ни в классе, ни в коллекции users, используем дефолтное значение
      setTeacher({
        uid: teacherId,
        displayName: 'Unknown teacher',
        email: null,
        photoURL: null
      });
    } catch (error) {
      console.error('Error fetching teacher information:', error);
      // В случае ошибки устанавливаем дефолтное значение
      setTeacher({
        uid: teacherId,
        displayName: 'Unknown teacher',
        email: null,
        photoURL: null
      });
    } finally {
      setLoadingTeacher(false);
    }
  }, [classId, user]);

  // Функция для загрузки информации об учениках
  const fetchStudentsInfo = async (studentIds: string[]) => {
    if (!studentIds.length) return;
    
    setLoadingStudents(true);
    try {
      const studentsData = [];
      
      for (const studentId of studentIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', studentId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            studentsData.push({
              id: studentId,
              displayName: userData.displayName || 'Unnamed student',
              email: userData.email || null,
              photoURL: userData.photoURL || null
            });
          } else {
            // Если документ пользователя не найден, добавляем базовую информацию
            studentsData.push({
              id: studentId,
              displayName: 'Unnamed student',
              email: null,
              photoURL: null
            });
          }
        } catch (error) {
          console.error(`Error fetching information about student ${studentId}:`, error);
        }
      }
      
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students information:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Добавляем новую функцию для загрузки колод класса
  const fetchClassDecks = async (deckIds: string[]) => {
    if (!deckIds || !deckIds.length) return;
    
    setLoadingClassDecks(true);
    try {
      const decksData: Deck[] = [];
      
      for (const deckId of deckIds) {
        try {
          const deck = await getDeckById(deckId);
          if (deck) {
            decksData.push(deck);
          }
        } catch (error) {
          console.error(`Error fetching information about deck ${deckId}:`, error);
        }
      }
      
      setClassDecks(decksData);
    } catch (error) {
      console.error('Error fetching class decks:', error);
    } finally {
      setLoadingClassDecks(false);
    }
  };

  // В основном useEffect добавляем загрузку колод класса
  useEffect(() => {
    const fetchClass = async () => {
      if (!classId) return;

      try {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        if (classDoc.exists()) {
          // Получаем данные и инициализируем пустые массивы, если они отсутствуют
          const classDocData = classDoc.data();
          
          const classDataWithId = {
            id: classDoc.id,
            ...classDocData,
            // Убедимся, что массивы существуют
            students: classDocData.students || [],
            deckIds: classDocData.deckIds || []
          } as Class;
          
          setClassData(classDataWithId);
          
          // Получаем информацию о преподавателе
          if (classDocData.teacherId) {
            fetchTeacherInfo(classDocData.teacherId);
          }
          
          // Загружаем информацию об учениках, если текущий пользователь - преподаватель
          if (user && classDocData.teacherId === user.uid && classDocData.students && classDocData.students.length > 0) {
            fetchStudentsInfo(classDocData.students);
          }
          
          // Загружаем информацию о колодах класса
          if (classDocData.deckIds && classDocData.deckIds.length > 0) {
            fetchClassDecks(classDocData.deckIds);
          }
          
          // Automatically open QR dialog if qr=true in URL and user is the teacher
          if (showQr && classDoc.data().teacherId === user?.uid) {
            setQrDialogOpen(true);
          }
        } else {
          navigate('/classes');
        }
      } catch (error) {
        console.error('Error fetching class:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClass();
  }, [classId, navigate, showQr, user, fetchTeacherInfo]);

  useEffect(() => {
    // Check if Web Share API is available
    const isShareAvailable = 'share' in navigator;
    setCanShare(isShareAvailable);
  }, []);

  // Загрузка колод пользователя
  useEffect(() => {
    const fetchUserDecks = async () => {
      if (user && isTeacher) {
        setLoadingDecks(true);
        try {
          const decks = await getUserDecks(user.uid);
          setUserDecks(decks);
        } catch (error) {
          console.error('Error fetching user decks:', error);
        } finally {
          setLoadingDecks(false);
        }
      }
    };

    fetchUserDecks();
  }, [user, isTeacher]);

  useEffect(() => {
    if (classData && classData.teacherId) {
      fetchTeacherInfo(classData.teacherId);
    }
  }, [classData, fetchTeacherInfo]);

  const handleJoinClass = async () => {
    if (!user || !classId) {
      console.error('Error joining class: missing user or classId', { user, classId });
      return;
    }
    
    setJoiningClass(true);
    try {
      // Check if class exists
      const classRef = doc(db, 'classes', classId);
      const classSnapshot = await getDoc(classRef);
      
      if (!classSnapshot.exists()) {
        throw new Error('Class not found');
      }
      
      // Get current class data
      const classData = classSnapshot.data();
      const students = classData.students || [];
      
      // Check if user is already in the class
      if (students.includes(user.uid)) {
        setSnackbar({
          open: true,
          message: 'You are already a member of this class',
          severity: 'info'
        });
        setJoiningClass(false);
        return;
      }
      
      // Update class document, adding user to students array
      await updateDoc(classRef, {
        students: arrayUnion(user.uid)
      });
      
      // Add class decks to user's deck list
      if (classData.deckIds && classData.deckIds.length > 0) {
        for (const deckId of classData.deckIds) {
          try {
            // Get deck by ID
            const deck = await getDeckById(deckId);
            if (deck) {
              // Add note that deck is from class
              const deckWithClassInfo = {
                ...deck,
                fromClassId: classId,
                fromClassName: classData.name,
                isPublic: deck.isPublic
              };
              
              // Add deck to user
              await addUserDeck(user.uid, deckWithClassInfo);
            }
          } catch (deckError) {
            console.error(`Error adding deck ${deckId} to user:`, deckError);
          }
        }
      }
      
      try {
        const userClassesRef = doc(db, 'userClasses', user.uid);
        const userClassesSnap = await getDoc(userClassesRef);
        
        if (userClassesSnap.exists()) {
          await updateDoc(userClassesRef, {
            classesJoined: arrayUnion(classId)
          });
        } else {
          await setDoc(userClassesRef, {
            classesJoined: [classId]
          });
        }
      } catch (userClassError) {
        console.error('Error updating userClasses:', userClassError);
        // Continue execution, as the main action (adding to class) has already happened
      }
      
      // Update local state
      if (classData) {
        setClassData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            students: [...(prevData.students || []), user.uid]
          };
        });
      }
      
      setSnackbar({
        open: true,
        message: 'You have successfully joined the class!',
        severity: 'success'
      });
      
      // Reload class data to update interface
      window.location.reload();
      
    } catch (error) {
      const errorDetails = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Error joining class: ${errorDetails}`, error);
      setSnackbar({
        open: true,
        message: `Failed to join class: ${errorDetails}`,
        severity: 'error'
      });
    } finally {
      setJoiningClass(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleOpenQrDialog = () => {
    setQrDialogOpen(true);
  };
  
  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
  };
  
  const handleSnackbarMessage = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteClass = async () => {
    if (!user || !classId) return;

    setIsDeleting(true);
    try {
      const success = await deleteClass(classId, user.uid);
      if (success) {
        setSnackbar({
          open: true,
          message: 'Class successfully deleted',
          severity: 'success'
        });
        
        // Redirect to the main page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      let errorMessage = 'Failed to delete class. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('Only the teacher')) {
          errorMessage = 'Only the teacher can delete this class.';
        } else if (error.message.includes('not found')) {
          errorMessage = 'Class not found.';
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Handle leaving the class
  const handleLeaveClass = async () => {
    if (!user || !classData) return;
    
    setLeavingClass(true);
    try {
      // Remove user from class members
      const classRef = doc(db, 'classes', classId as string);
      
      // Update the class document to remove the user from students array
      await updateDoc(classRef, {
        students: arrayRemove(user.uid)
      });

      // Удаляем колоды класса из библиотеки пользователя
      if (classData.deckIds && classData.deckIds.length > 0) {
        for (const deckId of classData.deckIds) {
          try {
            // Получаем информацию о колоде из библиотеки пользователя
            const userDecksRef = collection(db, 'userDecks', user.uid, 'decks');
            const docRef = doc(userDecksRef, deckId);
            const deckDoc = await getDoc(docRef);
            
            // Проверяем, есть ли колода в библиотеке пользователя и имеет ли она привязку к этому классу
            if (deckDoc.exists()) {
              const data = deckDoc.data();
              if (data.fromClassId === classId) {
                // Если колода не выбрана для сохранения, удаляем ее
                if (!selectedDecksToSave.includes(deckId)) {
                  await deleteDoc(docRef);
                }
              }
            }
          } catch (err) {
            console.error(`Failed to remove deck ${deckId} from user library:`, err);
          }
        }
      }

      // Process selected decks to save if any
      if (selectedDecksToSave.length > 0 && classDecks.length > 0) {
        for (const deckId of selectedDecksToSave) {
          try {
            // Find the deck object in class decks
            const deckToSave = classDecks.find(d => d.id === deckId);
            if (deckToSave) {
              // Проверяем, является ли колода публичной
              if (deckToSave.isPublic) {
                // Создаем копию колоды без привязки к классу
                const deckWithoutClassInfo = {
                  ...deckToSave,
                  fromClassId: undefined,
                  fromClassName: undefined
                };
                
                // Add deck to user's collection
                await addUserDeck(user.uid, deckWithoutClassInfo);
              } else {
                console.log(`Deck ${deckId} is not public and cannot be saved after leaving class`);
              }
            }
          } catch (err) {
            console.error(`Failed to save deck ${deckId}:`, err);
            // Continue with other decks even if one fails
          }
        }
      }
      
      // Navigate back to classes page
      setSnackbar({
        open: true,
        message: t('messages.leftClass'),
        severity: 'success'
      });
      navigate('/classes');
    } catch (error) {
      console.error('Error leaving class:', error);
      setSnackbar({
        open: true,
        message: `${t('common.error')}: ${error instanceof Error ? error.message : t('common.unexpectedError')}`,
        severity: 'error'
      });
    } finally {
      setLeavingClass(false);
      setLeaveDialogOpen(false);
    }
  };

  const handleCloseLeaveDialog = () => {
    setLeaveDialogOpen(false);
    setSelectedDecksToSave([]);
  };

  const handleOpenLeaveDialog = () => {
    // Initialize with empty selection
    setSelectedDecksToSave([]);
    setLeaveDialogOpen(true);
  };

  // Обработчик открытия диалога редактирования
  const handleOpenEditDialog = () => {
    if (classData) {
      setEditedClassName(classData.name);
      setEditedClassDescription(classData.description);
      setEditedDeckIds(classData.deckIds || []);
      setEditDialogOpen(true);
    }
  };

  // Обработчик закрытия диалога редактирования
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  // Обработчик сохранения изменений
  const handleSaveClassChanges = async () => {
    if (!user || !classId || !classData) return;
    
    // Проверка, является ли пользователь учителем
    if (classData.teacherId !== user.uid) {
      setSnackbar({
        open: true,
        message: t('classes.onlyTeacherCanEdit'),
        severity: 'error'
      });
      return;
    }
    
    setIsEditing(true);
    try {
      const classRef = doc(db, 'classes', classId);
      await updateDoc(classRef, {
        name: editedClassName,
        description: editedClassDescription,
        deckIds: editedDeckIds
      });
      
      // Обновление локального состояния
      setClassData({
        ...classData,
        name: editedClassName,
        description: editedClassDescription,
        deckIds: editedDeckIds
      });
      
      setSnackbar({
        open: true,
        message: t('messages.classUpdated'),
        severity: 'success'
      });
      
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating class:', error);
      setSnackbar({
        open: true,
        message: t('messages.classUpdateFailed'),
        severity: 'error'
      });
    } finally {
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!classData) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      {/* Header with class-specific styling */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mt: 3, 
          mb: 3, 
          borderTop: '5px solid #3f51b5', // Purple/blue border as class indicator
          position: 'relative',
          backgroundColor: '#f5f7ff' // Light blue-ish background
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <SchoolIcon sx={{ fontSize: 28, mr: 1, color: '#3f51b5' }} />
          <Typography variant="overline" sx={{ color: '#3f51b5', fontWeight: 'bold', letterSpacing: 1 }}>
            {t('classes.class')}
          </Typography>
        </Box>
        
        {/* Перемещаем кнопки управления наверх рядом с кнопкой "Все классы" */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/classes')}
          >
            {t('classes.allClasses')}
          </Button>
          
          {isTeacher && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isMobile ? (
                <>
                  <IconButton
                    color="primary"
                    onClick={handleOpenQrDialog}
                    size="small"
                  >
                    <QrCodeIcon />
                  </IconButton>
                  <IconButton
                    color="primary" 
                    onClick={handleOpenEditDialog}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={handleOpenDeleteDialog}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<QrCodeIcon />}
                    onClick={handleOpenQrDialog}
                  >
                    {t('classes.showQrCode')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEditDialog}
                    sx={{ mx: 1 }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenDeleteDialog}
                  >
                    {t('classes.deleteClass')}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {classData?.name || t('common.loading')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {classData.description}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ my: 4 }}>
        {user ? (
          <>
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('classes.classInformation')}
                </Typography>
                <List>
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {loadingTeacher ? (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      ) : (
                        teacher?.photoURL ? (
                          <Avatar src={teacher.photoURL} sx={{ mr: 2, width: 32, height: 32 }} />
                        ) : (
                          <PersonIcon sx={{ mr: 2 }} />
                        )
                      )}
                      <ListItemText
                        primary={t('classes.teacher')}
                        secondary={
                          loadingTeacher ? t('common.loading') :
                          isTeacher ? t('common.you') :
                          teacher?.displayName || teacher?.email || t('classes.unknownTeacher')
                        }
                      />
                    </Box>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary={t('classes.numberOfStudents')}
                      secondary={classData.students.length}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary={t('classes.numberOfDecks')}
                      secondary={classData.deckIds.length}
                    />
                  </ListItem>
                </List>
              </Paper>

              {/* Список учеников - видно только преподавателю */}
              {isTeacher && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('classes.students')} ({students.length})
                  </Typography>
                  
                  {loadingStudents ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : students.length > 0 ? (
                    <List>
                      {students.map((student) => (
                        <React.Fragment key={student.id}>
                          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                              {student.photoURL ? (
                                <Avatar src={student.photoURL} sx={{ mr: 2, width: 32, height: 32 }} />
                              ) : (
                                <PersonIcon sx={{ mr: 2 }} />
                              )}
                              <ListItemText
                                primary={student.displayName}
                                secondary={student.email || ''}
                              />
                            </Box>
                            
                            {/* Статистика обучения */}
                            {classData.deckIds.length > 0 && (
                              <Box sx={{ width: '100%', pl: 6, pr: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  {t('classes.learningProgress')}:
                                </Typography>
                                
                                {classDecks.map(deck => (
                                  <DeckProgressItem 
                                    key={deck.id}
                                    studentId={student.id}
                                    deckId={deck.id}
                                    deckTitle={deck.title}
                                  />
                                ))}
                              </Box>
                            )}
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 2 }}>
                      {t('classes.noStudentsYet')}
                    </Typography>
                  )}
                </Paper>
              )}

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('classes.availableDecks')}
                </Typography>
                
                {loadingClassDecks ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : classDecks.length > 0 ? (
                  <List>
                    {classDecks.map((deck) => (
                      <ListItemButton 
                        key={deck.id} 
                        onClick={() => {
                          // Если пользователь - автор колоды, перенаправляем на страницу редактирования
                          // Иначе перенаправляем на страницу изучения
                          if (user && deck.userId === user.uid) {
                            navigate(`/decks/${deck.id}`);
                          } else {
                            navigate(`/study/${deck.id}`);
                          }
                        }}
                        sx={{ 
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' } 
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={deck.title}
                            secondary={`${deck.stats?.totalCards || 0} ${t('decks.cards')}`}
                          />
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 2 }}>
                    {t('classes.noDecksYet')}
                  </Typography>
                )}
              </Paper>

              {!isTeacher && !isStudent && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleJoinClass}
                    disabled={joiningClass}
                  >
                    {joiningClass ? t('classes.joining') : t('classes.joinClass')}
                  </Button>
                </Box>
              )}
              
              {isStudent && !isTeacher && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('classes.youAreMember')}
                  </Alert>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleOpenLeaveDialog}
                      disabled={leavingClass}
                    >
                      {leavingClass ? t('classes.leavingClass') : t('classes.leaveClass')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Stack>
          </>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/classes')}
                sx={{ mb: 2 }}
              >
                {t('classes.myClasses')}
              </Button>
            </Box>
            <Stack spacing={3}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  {t('classes.welcomeToClass', { className: classData.name })}
                </Typography>
                <Typography variant="body1" paragraph>
                  {classData.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  {t('classes.joinAndStartLearning')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('classes.classDescription')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
                  {t('classes.signInToAccess')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate(`/signin?returnUrl=${encodeURIComponent(`/classes/${classId}`)}`)}
                  sx={{ mt: 2, px: 4, py: 1 }}
                >
                  {t('auth.signInTitle')}
                </Button>
              </Paper>
            </Stack>
          </>
        )}
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disableEnforceFocus
        disablePortal
      >
        <DialogTitle id="delete-dialog-title">
          {t('classes.confirmClassDeletion')}
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {t('classes.confirmDeleteClassMessage', { className: classData?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteClass} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('classes.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* QR Code Dialog */}
      <QrCodeDialog 
        open={qrDialogOpen}
        onClose={handleCloseQrDialog}
        url={classUrl}
        title={t('classes.qrCodeForClass')}
        onSnackbarMessage={handleSnackbarMessage}
        className={classId as string}
      />
      
      {/* Edit Class Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Class Information</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Class Name"
              type="text"
              fullWidth
              variant="outlined"
              value={editedClassName}
              onChange={(e) => setEditedClassName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Class Description"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={editedClassDescription}
              onChange={(e) => setEditedClassDescription(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="decks-select-label">Available Decks</InputLabel>
              <Select
                labelId="decks-select-label"
                multiple
                value={editedDeckIds}
                onChange={(e) => setEditedDeckIds(e.target.value as string[])}
                input={<OutlinedInput label="Available Decks" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((deckId) => {
                      const deck = userDecks.find(d => d.id === deckId);
                      return (
                        <Chip
                          key={deckId}
                          label={deck?.title || deckId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {loadingDecks ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography>Loading decks...</Typography>
                    </Box>
                  </MenuItem>
                ) : userDecks.length === 0 ? (
                  <MenuItem disabled>You don't have any available decks</MenuItem>
                ) : (
                  userDecks.map((deck) => (
                    <MenuItem key={deck.id} value={deck.id}>
                      {deck.title}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isEditing}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveClassChanges} 
            color="primary" 
            variant="contained"
            disabled={isEditing || !editedClassName.trim()}
            startIcon={isEditing ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog for leaving class with deck saving options */}
      <Dialog
        open={leaveDialogOpen}
        onClose={handleCloseLeaveDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Leave Class</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to leave this class?
          </Typography>
          {classData?.deckIds?.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Would you like to keep any public class decks in your personal collection?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Only public decks can be kept after leaving the class.
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="save-decks-label">Save Decks</InputLabel>
                <Select
                  labelId="save-decks-label"
                  multiple
                  value={selectedDecksToSave}
                  onChange={(e) => setSelectedDecksToSave(e.target.value as string[])}
                  input={<OutlinedInput label="Save Decks" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((deckId) => {
                        const deck = classDecks.find(d => d.id === deckId);
                        return (
                          <Chip
                            key={deckId}
                            label={deck?.title || deckId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {classDecks
                    .filter(deck => deck.isPublic) // Фильтруем только публичные колоды
                    .map((deck) => (
                    <MenuItem key={deck.id} value={deck.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>{deck.title}</Typography>
                        {deck.isPublic && (
                          <Tooltip title="Public deck">
                            <PublicIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                  {classDecks.filter(deck => deck.isPublic).length === 0 && (
                    <MenuItem disabled>No public decks available to save</MenuItem>
                  )}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeaveDialog} disabled={leavingClass}>
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveClass} 
            color="primary" 
            variant="contained"
            disabled={leavingClass}
            startIcon={leavingClass ? <CircularProgress size={20} /> : null}
          >
            {leavingClass ? 'Leaving...' : 'Leave Class'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Добавим новый компонент для отображения прогресса по колоде
interface DeckProgressItemProps {
  studentId: string;
  deckId: string;
  deckTitle: string;
}

const DeckProgressItem: React.FC<DeckProgressItemProps> = ({ studentId, deckId, deckTitle }) => {
  const [progress, setProgress] = useState<{
    cardsLearned: number;
    totalCards: number;
    lastStudied: Date | null;
    progress: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const userProgress = await getUserProgressForDeck(studentId, deckId);
        setProgress(userProgress);
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [studentId, deckId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
        <CircularProgress size={16} sx={{ mr: 1 }} />
        <Typography variant="body2">Loading...</Typography>
      </Box>
    );
  }

  if (!progress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
        <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>{deckTitle}</Typography>
        <Typography variant="body2" color="text.secondary">Not started</Typography>
      </Box>
    );
  }

  // Determine color based on progress
  let progressColor = 'error.main'; // red
  if (progress.progress >= 75) {
    progressColor = 'success.main'; // green
  } else if (progress.progress >= 40) {
    progressColor = 'warning.main'; // yellow
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', my: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" noWrap sx={{ flexGrow: 1, maxWidth: '50%' }}>{deckTitle}</Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {progress.cardsLearned}/{progress.totalCards} cards
          </Typography>
          
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <CircularProgress
              variant="determinate"
              value={progress.progress}
              size={24}
              thickness={5}
              sx={{ color: progressColor }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                {progress.progress}%
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      
      {progress.lastStudied && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mt: 0.5 }}>
          Last studied: {progress.lastStudied.toLocaleDateString()} {progress.lastStudied.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
        </Typography>
      )}
    </Box>
  );
};

export default ClassDetailsPage; 